// routes/_app.$usernameRaw.tsx
//
// Server-rendered user-profile *layout* — the chrome (provider, header,
// outer container) shared by all `/@:username/*` tabs.
//
// Render strategy: same "real page tree SSR" pattern as
// `_app.train-lora.tsx` / `_app.market.tsx`. We render
// `<ClientUserProfileLayout>` directly on both server and client so
// `<Outlet />` consumers can rely on `<UserProfileProvider>` being
// present from the very first paint, eliminating the previous
// shell → dynamic-import → real-layout swap that visibly shifted the
// page when the live profile chrome (avatar / banner / tabs) replaced
// the SSR shell.
//
// Mechanics:
//   - Loader pre-fetches `User` via `fetchUserForSSR(username)`. The
//     same query (`GetUserInfoByUsername({ username })`) is what the
//     client's `<UserProfileLayout>` calls via
//     `useGetUserInfoByUsername`, so seeding the route-scoped Apollo
//     cache here makes that hook hit cache on first render.
//   - Apollo client is pinned via `useRef` keyed on the username so
//     React Router 7's hydrate-time `loaderData` re-deserialisation
//     doesn't rebuild the cache (which would briefly drop every
//     `useQuery` subscriber back to `loading: true` — same lesson as
//     the artwork / model / market routes).
//   - `HydrateFallback` returns `null` so the parent `_app` Suspense
//     never interposes its own fallback between SSR and hydrate.
import type { ApolloClient } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import debug from 'debug'
import { useRef } from 'react'
import { useLoaderData } from 'react-router'
import ClientUserProfileLayout from 'src/clientRoutes/_app.$usernameRaw'
import {
  GetUserInfoByUsernameDocument,
  type GetUserInfoByUsernameQuery,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'
import { userProfileHandle } from 'src/utils/router/route'
import type { Route } from './+types/_app.$usernameRaw'

const log = debug('pixai:ssr-meta')

export const handle = userProfileHandle

export async function loader({ params, request }: Route.LoaderArgs) {
  const { fetchUserForSSR } =
    await import('src/utils/ssr-meta/graphql/user.server')
  const { parseUsername } = await import('src/utils/ssr-meta/url.server')

  const usernameRaw = params.usernameRaw
  if (!usernameRaw) {
    log('user layout loader missing usernameRaw url=%s', request.url)
    return { user: null as GetUserInfoByUsernameQuery['user'] | null }
  }
  const username = parseUsername(usernameRaw)
  if (!username) {
    log(
      'user layout loader invalid usernameRaw=%s url=%s',
      usernameRaw,
      request.url,
    )
    return { user: null }
  }
  const user = await fetchUserForSSR(username).catch((error: unknown) => {
    log('user layout loader fetch failed username=%s err=%o', username, error)
    return null
  })
  return { user, username }
}

export function HydrateFallback() {
  return null
}

export default function UserProfileLayoutRoute() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const user = loaderData.user
  const username = 'username' in loaderData ? loaderData.username : null

  // Pin the route-scoped Apollo client per `username`. Re-deserialisation
  // of `loaderData` after hydration still produces the same primitive
  // username string, so this stays stable across the SSR → hydrate
  // boundary (no cache rebuild, no flash). Switching to a different
  // user (e.g. SPA navigation between profiles) bumps the key and
  // rebuilds the client to avoid leaking the previous user's cache.
  const clientRef = useRef<{ id: string; client: ApolloClient } | null>(null)
  if (username && clientRef.current?.id !== username) {
    const seeds: Parameters<typeof createRouteApolloClient>[0] = user
      ? [
          {
            query: GetUserInfoByUsernameDocument,
            variables: { username },
            data: { user } satisfies GetUserInfoByUsernameQuery,
          },
        ]
      : []
    clientRef.current = { id: username, client: createRouteApolloClient(seeds) }
  }

  if (!clientRef.current) {
    // Username couldn't be parsed at the loader level. Render nothing —
    // the child route's loader will surface a proper not-found state.
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={clientRef.current.client}>
        <ClientUserProfileLayout />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
